import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ConfirmationDialog } from '@/app/components/ConfirmationDialog';
import { CreditNoteForm } from './CreditNoteForm';
import { CreditNoteFormValues } from './schema';
import { 
  getVoucher 
} from '@/app/services/voucherService';
import { listSites } from '@/app/services/siteService';
import { listItems } from '@/app/services/itemService';
import { listGodowns } from '@/app/services/inventoryService';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Loader2, AlertTriangle, CreditCard, Ban } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  createCreditNote, 
  updateCreditNote 
} from '@/app/services/creditNoteService';
import { VoucherResponse, VoucherCreate, VoucherUpdate } from '@/app/types/voucher';
import { Item } from '@/app/types/item';
import { RefundDialog } from '../components/RefundDialog';

const CreditNoteDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const fromInvoiceId = searchParams.get('fromInvoiceId');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditMode = !!id;

  // Hardcode the Credit Note voucher type ID - this is ID 5 from the provided data
  const creditNoteVoucherTypeId = 5;
  const [isSearchingItems, setIsSearchingItems] = useState(false);
  const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isCreateConfirmDialogOpen, setIsCreateConfirmDialogOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<CreditNoteFormValues | null>(null);

  // Fetch original Sales Invoice if 'fromInvoiceId' is present
  const { data: originalInvoice, isLoading: isLoadingOriginalInvoice } = useQuery({
    queryKey: ['originalSalesInvoice', fromInvoiceId],
    queryFn: () => fromInvoiceId ? getVoucher(Number(fromInvoiceId)) : Promise.resolve(null),
    enabled: !!fromInvoiceId && !isEditMode, // Only fetch if creating new and fromInvoiceId exists
  });

  // Fetch Credit Note if in edit mode
  const { data: creditNote, isLoading: isLoadingCreditNote, isError: isErrorCreditNote, error: errorCreditNote } = useQuery({
    queryKey: ['creditNote', id],
    queryFn: () => id ? getVoucher(Number(id)) : Promise.resolve(null),
    enabled: isEditMode,
  });

  // Fetch common data (sites, godowns)
  const { data: sitesData = { items: [] } } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites({ limit: 100 }),
  });
  
  const { data: godownsData = { items: [] } } = useQuery({
    queryKey: ['godowns'],
    queryFn: () => listGodowns({ limit: 100 }),
  });

  // Function to search items for product selection in the grid
  const searchItems = async (query: string): Promise<Item[]> => {
    if (!query || query.length < 2) return [];
    
    setIsSearchingItems(true);
    try {
      const result = await listItems({ search_query: query, limit: 20 });
      return result.items || [];
    } catch (error) {
      console.error('Error searching items:', error);
      return [];
    } finally {
      setIsSearchingItems(false);
    }
  };

  const mutationOptions = {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creditNotes'] });
      queryClient.invalidateQueries({ queryKey: ['creditNote', id] });
      if (fromInvoiceId) {
        queryClient.invalidateQueries({ queryKey: ['sales-invoice', fromInvoiceId] });
      }
      toast.success(`Credit Note ${isEditMode ? 'updated' : 'created'} successfully.`);
      navigate('/credit-notes/list');
    },
    onError: (error: Error) => {
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} Credit Note: ${error.message}`);
    },
  };

  const createCreditNoteMutation = useMutation({
    mutationFn: (data: VoucherCreate) => createCreditNote(data),
    ...mutationOptions,
  });

  const updateCreditNoteMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: VoucherUpdate }) => updateCreditNote(id, data),
    ...mutationOptions,
  });

  const handleSubmit = (formData: CreditNoteFormValues) => {
    if (!isEditMode) {
      // Show confirmation dialog for new credit notes
      setPendingFormData(formData);
      setIsCreateConfirmDialogOpen(true);
    } else {
      // For edits, proceed directly
      processSubmit(formData);
    }
  };

  const processSubmit = (formData: CreditNoteFormValues) => {
    // Format the data for API
    const payload = {
      ...formData,
      voucher_type_id: creditNoteVoucherTypeId,
      // Format dates as strings for API
      transaction_date: formData.transaction_date instanceof Date
        ? formData.transaction_date.toISOString().split('T')[0]
        : formData.transaction_date,
      is_cancelled: false,
    };

    // If creating from an original invoice, add reference_invoice_number to payload
    if (!isEditMode && fromInvoiceId && originalInvoice) {
      console.log('[CreditNoteDetailPage] Adding reference_invoice_number:', originalInvoice.voucher_number);
      (payload as VoucherCreate & { reference_invoice_number?: string }).reference_invoice_number = originalInvoice.voucher_number;
    }

    if (isEditMode && id) {
      updateCreditNoteMutation.mutate({ id: Number(id), data: payload as unknown as VoucherUpdate });
    } else {
      createCreditNoteMutation.mutate(payload as unknown as VoucherCreate);
    }
  };

  const confirmCreate = () => {
    if (pendingFormData) {
      processSubmit(pendingFormData);
      setIsCreateConfirmDialogOpen(false);
      setPendingFormData(null);
    }
  };
  
  const isLoading = (isEditMode && isLoadingCreditNote) || (!isEditMode && !!fromInvoiceId && isLoadingOriginalInvoice);
  const isSubmitting = createCreditNoteMutation.isPending || updateCreditNoteMutation.isPending;

  if (isLoading) {
    return <div className="flex justify-center items-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  if (isErrorCreditNote && isEditMode) {
    return <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>Error loading credit note: {(errorCreditNote as Error)?.message}</AlertDescription></Alert>;
  }

  // Determine initial data for the form
  let initialData: Partial<VoucherResponse> | undefined = undefined;
  if (isEditMode && creditNote) {
    initialData = creditNote;
  } else if (originalInvoice) {
    // Use the original invoice number directly as the reference number
    const referenceNumber = originalInvoice.voucher_number;
    
    // Log for debugging
    console.log('[CreditNoteDetailPage] Original voucher_number:', originalInvoice.voucher_number);
    console.log('[CreditNoteDetailPage] Using reference number:', referenceNumber);
    
    // Pre-fill from original invoice
    initialData = {
      party_id: originalInvoice.party_id,
      site_id: originalInvoice.site_id,
      reference_number: referenceNumber,
      // Add reference_invoice_number to store the original invoice number for backend
      reference_invoice_number: originalInvoice.voucher_number, 
      // Use reference_id to track original invoice id internally
      reference_id: originalInvoice.id,
      items: originalInvoice.items?.map(item => ({
        ...item,
        id: undefined, // This is a new credit note item
        // Store the original invoice item ID as a custom field
        original_invoice_item_id: item.id, 
        quantity: item.quantity, // User will adjust this as needed
      })) || [],
      // Set current date
      transaction_date: new Date().toISOString(),
    } as Partial<VoucherResponse> & { reference_invoice_number?: string, reference_id?: number };
    
    // Log to ensure we're passing party_id correctly
    console.log('[CreditNoteDetailPage] Initialized with party_id:', initialData.party_id);
    console.log('[CreditNoteDetailPage] Set reference number:', referenceNumber);
  }

  // Check if credit note can be refunded
  const canProcessRefund = isEditMode && creditNote &&
    !creditNote.is_cancelled &&
    creditNote.status !== 'Paid' &&
    creditNote.payment_status !== 'Paid' &&
    creditNote.total_amount > 0;

  // Check if credit note can be cancelled (only in Draft status)
  const canCancel = isEditMode && creditNote &&
    !creditNote.is_cancelled &&
    ((creditNote as any).status === 'Draft' || !(creditNote as any).status) &&
    (creditNote as any).payment_status !== 'Paid';

  // Determine status for display
  const getStatusBadge = () => {
    if (!creditNote) return null;

    if (creditNote.is_cancelled) {
      return <Badge variant="destructive">Cancelled</Badge>;
    } else if (creditNote.payment_status === 'Paid' || creditNote.status === 'Paid') {
      return <Badge variant="secondary">Refunded</Badge>;
    } else if (creditNote.status === 'Posted') {
      return <Badge variant="outline">Posted</Badge>;
    } else {
      return <Badge variant="default">Draft</Badge>;
    }
  };

  // Check if form should be locked
  const isFormLocked = creditNote?.is_cancelled ||
    creditNote?.status === 'Paid' ||
    creditNote?.payment_status === 'Paid';

  const handleProcessRefund = () => {
    if (creditNote) {
      setIsRefundDialogOpen(true);
    }
  };

  const handleCancel = () => {
    if (creditNote) {
      setIsCancelDialogOpen(true);
    }
  };

  const confirmCancel = async () => {
    if (!creditNote || !id) return;

    try {
      const { cancelVoucher } = await import('@/app/services/voucherService');
      await cancelVoucher(Number(id));
      queryClient.invalidateQueries({ queryKey: ['creditNote', id] });
      queryClient.invalidateQueries({ queryKey: ['creditNotes'] });
      toast.success('Credit note cancelled successfully. Accounting entries have been reversed.');
      setIsCancelDialogOpen(false);
    } catch (error) {
      toast.error(`Failed to cancel credit note: ${(error as Error).message}`);
      setIsCancelDialogOpen(false);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(isEditMode ? '/credit-notes/list' : '/sales-invoices/list')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight">
              {isEditMode ? `Credit Note ${creditNote?.voucher_number}` : 'Create Credit Note'}
              {fromInvoiceId && !isEditMode && originalInvoice && ` for Invoice ${originalInvoice.voucher_number}`}
            </h1>
            {isEditMode && getStatusBadge()}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Cancel Button */}
          {canCancel && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              <Ban className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          )}

          {/* Process Refund Button */}
          {canProcessRefund && (
            <Button
              variant="default"
              size="sm"
              onClick={handleProcessRefund}
              disabled={isSubmitting}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Process Refund
            </Button>
          )}
        </div>
      </div>

      {fromInvoiceId && originalInvoice && !isEditMode && (
        <Alert>
          <AlertDescription>
            Creating credit note for Invoice #{originalInvoice.voucher_number} from {originalInvoice.party?.name}.
            Customer and site details are automatically populated.
          </AlertDescription>
        </Alert>
      )}

      {isFormLocked && isEditMode && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            This credit note has been {creditNote?.is_cancelled ? 'cancelled' : 'refunded'} and cannot be modified.
          </AlertDescription>
        </Alert>
      )}

      {(sitesData.items && godownsData.items) && (
        <CreditNoteForm
          creditNote={initialData}
          sites={sitesData.items}
          godowns={godownsData.items}
          voucherTypeId={creditNoteVoucherTypeId}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          isDisabled={isFormLocked}
          searchItems={searchItems}
          isLoadingItems={isSearchingItems}
          originalInvoiceItems={originalInvoice?.items || []}
          originalInvoiceParty={originalInvoice?.party}
        />
      )}

      {/* Refund Dialog */}
      {creditNote && (
        <RefundDialog
          isOpen={isRefundDialogOpen}
          onClose={() => setIsRefundDialogOpen(false)}
          creditNote={creditNote}
        />
      )}

      {/* Cancel Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        onConfirm={confirmCancel}
        title={`Cancel Credit Note ${creditNote?.voucher_number}?`}
        description={`This action will cancel the credit note and reverse all associated accounting entries.\n\nThis action cannot be undone.`}
        confirmText="Yes, Cancel Credit Note"
        cancelText="No, Keep It"
        isLoading={false}
        variant="destructive"
      />

      {/* Create Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isCreateConfirmDialogOpen}
        onClose={() => {
          setIsCreateConfirmDialogOpen(false);
          setPendingFormData(null);
        }}
        onConfirm={confirmCreate}
        title="Create Credit Note?"
        description={`Creating this credit note will immediately:\n\n• Generate accounting entries (Debit: Customer Account, Credit: Sales Return)\n• Reduce the customer's outstanding balance\n• Allow for refund processing\n\nPlease ensure all details are correct before proceeding.`}
        confirmText="Yes, Create Credit Note"
        cancelText="Review Again"
        isLoading={createCreditNoteMutation.isPending}
      />
    </div>
  );
};

export default CreditNoteDetailPage; 